/* eslint-disable @typescript-eslint/no-unused-vars */
import type
{
  CaseDetail,
  CaseDetailAddDTO,
  CaseFolder,
  CaseFolderObject,
  SiteModel,
  UploadInitResponse
} from '../models/DataAdminTypes';
import { CompletedPart, PresignedPostData } from './ClientMultipartUploader';

import type IDataAdminService from './IDataAdminService';
import getSitesMockResponse from '../__tests__/mockData/getSitesMockResponse.json';

const RETRY_TEST_MODE = 'test-retry';

export default class MockDataAdminService implements IDataAdminService {
  mode: string;

  initFileUploadExecCount = 0;
  getMultiPartPresignedUrlExecCount = 0;
  completedMultipartUploadExecCount = 0;

  constructor(mode: string) {
    this.mode = mode;
  }

  clearCache(): void {
  }

  async getCaseDetailsForSite(accessToken: string, siteCode: string, onlyStaged: boolean = false): Promise<CaseDetail[]> {
    const mockCases: CaseDetail[] = [
      {
        caseId: 'MOCK-CASE-001',
        caseDate: '2023-10-01',
        caseFolder: `${siteCode}/MOCK-CASE-001_2023-10-01`,
        dataBucket: `${siteCode}-data`,
        isRevision: false,
        isNavigated: true,
        isStaged: true, // This one is staged
        createdBy: 'mock-user',
        updatedBy: 'mock-user',
        createdAt: new Date('2023-10-01T08:00:00Z'),
        updatedAt: new Date('2023-10-02T14:00:00Z')
      },
      {
        caseId: 'MOCK-CASE-002',
        caseDate: '2023-11-15',
        caseFolder: `${siteCode}/MOCK-CASE-002_2023-11-15`,
        dataBucket: `${siteCode}-data`,
        isRevision: false,
        isNavigated: false,
        isStaged: false, // This one is NOT staged
        createdBy: 'mock-user',
        updatedBy: 'mock-user',
        createdAt: new Date('2023-11-15T09:00:00Z'),
        updatedAt: new Date('2023-11-15T09:00:00Z')
      }
    ];

    if (onlyStaged) {
      return mockCases.filter(c => c.isStaged);
    }

    return mockCases;
  }

  async completeMultipartUpload(
    accessToken: string,
    siteCode:
      string, caseFolder: string,
    objectKey: string,
    uploadId: string,
    completedParts: CompletedPart[]
  ): Promise<PresignedPostData> {
    this.completedMultipartUploadExecCount += 1;
    if (this.mode === RETRY_TEST_MODE && this.completedMultipartUploadExecCount === 1) {
      throw new Error('Simulated transient error for testing retries');
    }
    return {
      uploadUrl: 'https://mock-upload-url.com',
      uploadFields: {
      }
    };
  }

  async createCaseDetail(accessToken: string, siteCode: string, caseDetail: CaseDetailAddDTO): Promise<CaseDetail> {
    return {
      caseId: caseDetail.caseId,
      caseDate: caseDetail.caseDate,
      caseFolder: `${siteCode}-${caseDetail.caseId}-${caseDetail.caseDate}`,
      dataBucket: `${siteCode}-data-bucket`,
      isRevision: caseDetail.isRevision,
      isNavigated: caseDetail.isNavigated,
      isStaged: caseDetail.isStaged,
      createdBy: 'mockUser',
      updatedBy: 'mockUser',
      createdAt: new Date(),
      updatedAt: new Date()
    } as CaseDetail;
  }

  async createCaseFolder(accessToken: string, siteCode: string, caseId: string, startDate: string): Promise<string> {
    return `${siteCode}-${caseId}-${startDate}`;
  }

  async createSubFolder(
    accessToken: string, siteCode: string, caseFolder: string, subFolderPath: string
  ): Promise<string> {
    return subFolderPath.trim();
  }

  async getSites(accessToken: string): Promise<SiteModel[]> {
    return getSitesMockResponse;
  }

  async getServiceHealth(): Promise<object> {
    return { status: 'ok', message: 'Mock service is healthy' };
  }

  async getCaseFoldersForSite(accessToken: string, siteCode: string): Promise<CaseFolder[]> {
    return [];
  }

  async getFolderObjects(accessToken: string, siteCode: string, folder: string): Promise<CaseFolderObject[]> {
    return [];
  }

  async getMultiPartPresignedUrl(
    accessToken: string, siteCode: string, bucket: string, partNumber: number, objectKey: string, uploadId: string
  ): Promise<string> {
    this.getMultiPartPresignedUrlExecCount += 1;
    if (this.mode === RETRY_TEST_MODE && this.getMultiPartPresignedUrlExecCount === 1) {
      throw new Error('Simulated transient error for testing retries');
    }
    return `https://mock-presigned-url.com/part/${partNumber}`;
  }

  async initFileUpload(
    accessToken: string, siteCode: string, bucket: string, path: string, fileSizeBytes: number
  ): Promise<UploadInitResponse> {
    this.initFileUploadExecCount += 1;
    if (this.mode === RETRY_TEST_MODE && this.initFileUploadExecCount === 1) {
      throw new Error('Simulated transient error for testing retries');
    }
    return { uploadId: 'mock-upload-id', partSize: 5 * 1024 * 1024, objectKey: `${siteCode}/mock-object-key` };
  }
};